import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import FullPageLoader from "../components/fullPageLoader/FullPageLoader";
import DownloadMiMComponent from "../components/profile/DownloadMiMComponent";
import { AccountContext, TokenContext } from "../constants/Contexts";
import { IServiceResponse } from "../interfaces/IServiceReponse";
import { IUser } from "../interfaces/IUser";
import AccountService from "../services/AccountService";
import {
  getUserError,
  getUserStarted,
  getUserSuccess,
  userLoginError,
  userLoginStarted,
  userLoginSuccess,
} from "../state/user/userActions";
import "./profileContainerComponent.scss";

const ProfileContainerComponent = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState<string>("");
  const [account, setAccount] = useState<IUser>();

  const getAccessToken = async () => {
    let token: string | undefined;

    try {
      dispatch(userLoginStarted());

      token = await getAccessTokenSilently();

      setAccessToken(token);

      dispatch(userLoginSuccess(token));
    } catch (err) {
      const errMessage = `Failed to get access token. Error: ${err}`;
      dispatch(userLoginError(errMessage));
    }

    return token;
  };

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      getAccessToken();
    } else {
      getAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const getAccount = useCallback(async () => {
    if (accessToken) {
      dispatch(getUserStarted());
      try {
        const accountService = new AccountService(accessToken);
        const response = await accountService.getUser();
        const serviceResponse: IServiceResponse = response.data;
        const user: IUser = {
          AccountId: serviceResponse.data["accountId"],
          FirstName: serviceResponse.data["firstName"],
          LastName: serviceResponse.data["lastName"],
          Email: serviceResponse.data["email"],
          OrganisationId:
            serviceResponse.data["organisation"]?.["organisationId"],
          OrganisationName:
            serviceResponse.data["organisation"]?.["organisationName"],
          ProfilePictureUrl: serviceResponse.data["profilePictureUrl"],
          AvatarUrl: serviceResponse.data["avatarUrl"],
          LicenceTypeName: serviceResponse.data["licence"]["licenceTypeName"],
          NextInvoiceDate: serviceResponse.data["licence"]["nextInvoiceDate"],
          IsNonExpiring: serviceResponse.data["licence"]["isNonExpiring"],
          Created: serviceResponse.data["created"],
          Suspended: serviceResponse.data["suspended"],
          Archived: serviceResponse.data["archived"],
          Token: accessToken,
        };
        setAccount(user);
        dispatch(getUserSuccess(user));
      } catch (error) {
        dispatch(getUserError(error));
        //need to handle error here like redirect to login?
        //or failed to get profile message
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]); // if token changes, callback will run again to get the account info

  return (
    <>
      {account ? (
        <TokenContext.Provider value={accessToken}>
          <AccountContext.Provider value={{ account, setAccount }}>
            <DownloadMiMComponent />
            <main className="main-profile-container bg-grey-light absolute inset-0">
              <Outlet />
            </main>
          </AccountContext.Provider>
        </TokenContext.Provider>
      ) : (
        <FullPageLoader />
      )}
    </>
  );
};

export default ProfileContainerComponent;
